import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  Wrench,
  AlertCircle,
  Check,
  Loader2,
} from "lucide-react";
import Cookies from "js-cookie";
import CryptoJS from "crypto-js";
import {
  APL_LINK,
  server_post_data,
  login_to_superadmin,
} from "../ServiceConnection/serviceconnection.js";
import { storeData } from "../LocalConnection/LocalConnection.js";

// ─── Constants ────────────────────────────────────────────────────────────────
const CRYPTO_SECRET = "secret_key";
const COOKIE_EXPIRY = 30; // days

// ─── Crypto helpers — same as old code ───────────────────────────────────────
const encryptData = (value) =>
  CryptoJS.AES.encrypt(
    value +
      "::" +
      CryptoJS.lib.WordArray.random(128 / 8).toString(CryptoJS.enc.Hex),
    CRYPTO_SECRET,
  ).toString();

const decryptData = (encrypted) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, CRYPTO_SECRET);
    return bytes.toString(CryptoJS.enc.Utf8).split("::")[0];
  } catch {
    return "";
  }
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const Login = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // ─── Load saved credentials from cookies ──────────────────────────────────
  useEffect(() => {
    const savedEmail = Cookies.get("email");
    const savedPassword = Cookies.get("password");
    if (savedEmail && savedPassword) {
      setEmail(decryptData(savedEmail));
      setPassword(decryptData(savedPassword));
      setRememberMe(true);
    }
  }, []);

  // ─── Remember Me toggle ───────────────────────────────────────────────────
  const handleRememberMeChange = () => {
    const next = !rememberMe;
    setRememberMe(next);
    if (!next) {
      Cookies.remove("email");
      Cookies.remove("password");
    }
  };

  // ─── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      e.email = "Enter a valid email";
    if (!password.trim()) e.password = "Password is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSaveChangesdynamic = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      setAuthError("");

      const fd = new FormData();
      fd.append("useremail", email.toLowerCase());
      fd.append("userpassword", password);

      const response = await server_post_data(login_to_superadmin, fd);

      if (!response.data.error) {
        const data_admin = response.data.message.data_admin;
        const data_designation = response.data.message.data_designation;
        const data_admin_image = response.data.message.data_admin_image;

        // ── Store session — same keys as existing LocalConnection ──────
        storeData("admin_id", data_admin.primary_id);
        storeData("admin_name", data_admin.admin_name);
        storeData("admin_email", data_admin.admin_email);
        storeData("admin_designation", data_admin.admin_designation);
        storeData("admin_type", data_admin.admin_type);
        storeData("allow_access", data_admin.allow_access);
        storeData(
          "admin_image",
          APL_LINK + data_admin_image + data_admin.admin_image,
        );
        storeData("designation_name", data_designation.designation_name);
        storeData("access_permission", data_designation.access_permission);

        // Gender prefix
        let show_mr = "";
        if (data_admin.admin_gender === "Male") show_mr = "Mr. ";
        else if (data_admin.admin_gender === "Female") show_mr = "Ms. ";
        storeData("admin_name_with_gender", show_mr + data_admin.admin_name);

        // ── Remember Me ────────────────────────────────────────────────
        if (rememberMe) {
          Cookies.set("email", encryptData(data_admin.admin_email), {
            expires: COOKIE_EXPIRY,
          });
          Cookies.set("password", encryptData(data_admin.admin_password), {
            expires: COOKIE_EXPIRY,
          });
        } else {
          Cookies.remove("email");
          Cookies.remove("password");
        }

        setSuccess(true);

        navigate("/dashboard"); // Admin → dashboard
      } else {
        setAuthError(response.data.message);
      }
    } catch (error) {
      console.error(error);
      setAuthError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Enter key ────────────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSaveChangesdynamic();
  };

  // ─── UI ───────────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen bg-background">
      {/* ── Left panel — branding ────────────────────────────────────── */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-sidebar p-12 lg:flex lg:w-[45%]">
        <div className="pointer-events-none absolute -left-20 -top-20 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -right-10 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-primary shadow-glow">
            <Wrench className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-lg font-bold leading-none text-white">
              Advance Collision
            </p>
            <p className="mt-0.5 text-xs leading-none text-white/40">
              &amp; Repair — Dashboard
            </p>
          </div>
        </div>

        {/* Center content */}
        <div className="relative">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-primary">
            Website Management
          </p>
          <h1 className="mb-5 text-4xl font-extrabold leading-tight text-white">
            Manage your
            <br />
            <span className="text-gradient">entire website</span>
            <br />
            from one place.
          </h1>
          <p className="max-w-sm text-sm leading-relaxed text-white/50">
            Update contact info, manage blog posts, view customer messages and
            estimate requests — all in one clean dashboard.
          </p>
          <div className="mt-8 space-y-3">
            {[
              "View contact & estimate form submissions",
              "Manage blog posts and FAQs",
              "Update business info and hours",
              "Manage customer testimonials",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/20">
                  <Check className="h-3 w-3 text-primary" />
                </div>
                <p className="text-sm text-white/60">{item}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative">
          <p className="text-xs text-white/20">
            © {new Date().getFullYear()} Advance Collision &amp; Repair. Des
            Plaines, IL.
          </p>
        </div>
      </div>

      {/* ── Right panel — form ───────────────────────────────────────── */}
      <div className="flex flex-1 flex-col items-center justify-center p-6 md:p-12">
        {/* Mobile logo */}
        <div className="mb-8 flex items-center gap-3 lg:hidden">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-primary shadow-glow">
            <Wrench className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-base font-bold leading-none text-foreground">
              ACR Dashboard
            </p>
            <p className="mt-0.5 text-xs leading-none text-muted-foreground">
              Advance Collision &amp; Repair
            </p>
          </div>
        </div>

        <div className="w-full max-w-sm">
          {/* Heading */}
          <div className="mb-8">
            <h2 className="text-2xl font-extrabold text-foreground md:text-3xl">
              Welcome back
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Sign in to your dashboard to continue.
            </p>
          </div>

          {/* Auth error */}
          {authError && (
            <div className="mb-5 flex items-center gap-3 rounded-2xl border border-destructive/20 bg-destructive/5 p-4">
              <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
              <p className="text-sm text-destructive">{authError}</p>
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="mb-5 flex items-center gap-3 rounded-2xl border border-success/20 bg-success/5 p-4">
              <Check className="h-4 w-4 shrink-0 text-success" />
              <p className="text-sm font-semibold text-success">
                Login successful! Redirecting...
              </p>
            </div>
          )}

          {/* Form */}
          <form
            id="loginFormData"
            onSubmit={handleSaveChangesdynamic}
            onKeyDown={handleKeyDown}
            className="space-y-4"
          >
            {/* useremail */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="email"
                  name="useremail"
                  placeholder="admin@advancecollision.com"
                  value={email}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\s/.test(val) || val.length > 72) return;
                    setEmail(val);
                    if (errors.email)
                      setErrors((prev) => ({ ...prev, email: null }));
                    if (authError) setAuthError("");
                  }}
                  className={`pl-9 ${errors.email ? "border-destructive" : ""}`}
                  autoComplete="email"
                  autoFocus
                  tabIndex={1}
                  maxLength={75}
                  minLength={3}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-xs text-destructive">{errors.email}</p>
              )}
            </div>

            {/* userpassword */}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-foreground">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type={showPass ? "text" : "password"}
                  name="userpassword"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password)
                      setErrors((prev) => ({ ...prev, password: null }));
                    if (authError) setAuthError("");
                  }}
                  className={`pl-9 pr-10 ${errors.password ? "border-destructive" : ""}`}
                  autoComplete="current-password"
                  tabIndex={2}
                  maxLength={40}
                  minLength={3}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  tabIndex={3}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                >
                  {showPass ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-destructive">
                  {errors.password}
                </p>
              )}
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  id="myCheckbox"
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                  tabIndex={5}
                  className="h-4 w-4 rounded accent-primary"
                />
                <span className="select-none text-sm text-muted-foreground">
                  Remember me
                </span>
              </label>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="mt-2 w-full gap-2 rounded-xl py-5 text-base font-semibold"
              disabled={loading || success}
              tabIndex={4}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Signing in...
                </>
              ) : success ? (
                <>
                  <Check className="h-4 w-4" /> Redirecting...
                </>
              ) : (
                "Sign In to Dashboard"
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
